import { Link, Navigate } from 'react-router-dom';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { StatsSkeleton } from '@/components/PageSkeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/date';
import { useCanvases } from '@/components/canvas/CanvasProvider';
import { CanvasNumber, ProgressBar, StatusBadge, TagChip, familyOf, minutesLabel, statusKey } from '@/components/canvas/bits';

export const route = { path: '/dashboard', layout: 'owner', access: "profile === 'Learner_c'" };
export const nav = { icon: 'LayoutDashboard', label: 'Dashboard', order: 10, profiles: ['Learner_c'] };

const ARCS = [
  { name: 'Make the case', blurb: 'Day 1. Choose your case and prove it is worth doing.', from: 0, to: 4, ink: 'text-(color:--arc-case-ink)', bar: 'bg-(color:--arc-case)' },
  { name: 'Make it buildable', blurb: 'Day 2. Design what has to exist, and the guardrails.', from: 5, to: 6, ink: 'text-(color:--arc-build-ink)', bar: 'bg-(color:--arc-build)' },
  { name: 'Make it happen', blurb: 'Day 2. People, order of work, and the pitch.', from: 7, to: 9, ink: 'text-(color:--arc-happen-ink)', bar: 'bg-(color:--arc-happen)' },
];

const ACTION = { 'Not started': 'Start', 'In progress': 'Continue', Confirmed: 'Open', Review: 'Review' };

function Tile({ icon, tint, label, value, sub }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs">
      <span className={cn('flex size-10 shrink-0 items-center justify-center rounded-xl text-white', tint)}>
        <ApperIcon name={icon} size={20} />
      </span>
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-semibold leading-tight tabular-nums">{value}</p>
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </div>
  );
}

function CanvasCard({ def, state }) {
  const fam = familyOf(def);
  const key = statusKey(state);
  return (
    <Link
      to={`/canvas/${def.no}`}
      className="group flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-xs outline-none transition-all duration-(--transition-fast) hover:border-primary/40 hover:shadow-md focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <div className="flex items-start gap-3">
        <CanvasNumber def={def} />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold leading-tight">{def.no === 0 ? 'Case Card' : def.title}</p>
          <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground">{def.question}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <StatusBadge state={state} />
        <span className="text-xs text-muted-foreground">{minutesLabel(def)}</span>
      </div>
      <ProgressBar value={state?.completion ?? 0} barClassName={fam.solid} label={`${def.title} completion`} />
      <p className="flex items-center gap-1 text-xs font-medium text-primary">
        {ACTION[key]}
        <ApperIcon name="ArrowRight" size={12} className="transition-transform group-hover:translate-x-0.5" />
      </p>
    </Link>
  );
}

export default function Dashboard() {
  const { loading, loadError, reload, needsWelcome, states, entries, agg, defs, profile, packReady } = useCanvases();

  if (needsWelcome) return <Navigate to="/welcome" replace />;

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6" aria-busy="true">
        <Skeleton className="h-9 w-72" />
        <StatsSkeleton count={4} />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="mx-auto max-w-xl space-y-4 rounded-2xl border border-destructive/40 bg-destructive/10 p-6">
        <h1 className="font-heading text-xl font-semibold">Your canvases did not load</h1>
        <p className="text-sm">{loadError}</p>
        <Button onClick={reload}>Try again</Button>
      </div>
    );
  }

  const list = defs.map((d) => ({ def: d, st: states[d.no] }));
  const confirmed = list.filter((x) => x.st?.confirmed).length;
  const overall = Math.round(list.reduce((a, x) => a + (x.st?.confirmed ? 100 : x.st?.completion ?? 0), 0) / defs.length);
  const stale = list.filter((x) => x.st?.stale);
  const gaps = agg.gaps;
  const gapsG = gaps.filter((g) => g.tag === 'G').length;
  const openRows = agg.ledger.filter((r) => r.status === 'Open');

  // where to go next: a canvas that needs review, then the one touched last, then the first not started
  const stamp = (no) => String(entries[no]?.modifiedOn || '');
  const touched = list.filter((x) => x.st?.started && !x.st?.confirmed).sort((a, b) => stamp(b.def.no).localeCompare(stamp(a.def.no)));
  const next = stale[0] || touched[0] || list.find((x) => !x.st?.confirmed) || null;
  const firstName = (profile.name || '').split(/\s+/)[0] || 'there';

  return (
    <div className="mx-auto max-w-6xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-heading text-3xl font-semibold text-balance">Hello, {firstName}</h1>
          <p className="text-muted-foreground">
            {profile.organisation ? `${profile.organisation}. ` : ''}
            {confirmed === defs.length
              ? 'All ten canvases are confirmed. Your pack is ready.'
              : `${confirmed} of ${defs.length} confirmed. Pick up where you left off.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {next && (
            <Button asChild size="lg">
              <Link to={`/canvas/${next.def.no}`}>
                {next.st?.stale ? 'Review' : next.st?.started ? 'Continue' : 'Start'} {next.def.no === 0 ? 'the Case Card' : `Canvas ${next.def.no}`}
                <ApperIcon name="ArrowRight" size={18} />
              </Link>
            </Button>
          )}
          <Button asChild size="lg" variant={packReady ? 'default' : 'outline'} className={packReady ? 'bg-highlight text-highlight-foreground hover:bg-highlight/90' : ''}>
            <Link to="/pack">
              <ApperIcon name="FileDown" size={18} />
              {packReady ? 'Download my pack' : 'My pack'}
            </Link>
          </Button>
        </div>
      </div>

      {stale.length > 0 && (
        <div className="flex gap-3 rounded-2xl border border-warning-border bg-warning-muted p-4" role="alert">
          <ApperIcon name="TriangleAlert" size={20} className="mt-0.5 shrink-0 text-warning" />
          <div className="space-y-1 text-sm">
            <p className="font-semibold">
              {stale.length === 1 ? 'One canvas needs a second look' : `${stale.length} canvases need a second look`}
            </p>
            <p>You changed an earlier canvas after confirming these. Check what they pulled in, then confirm them again.</p>
            <p className="flex flex-wrap gap-x-4 gap-y-1">
              {stale.map((x) => (
                <Link key={x.def.no} to={`/canvas/${x.def.no}`} className="font-medium underline underline-offset-2">
                  {x.def.no === 0 ? 'Case Card' : `Canvas ${x.def.no}`}
                </Link>
              ))}
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Tile icon="CircleCheck" tint="bg-(color:--step-3)" label="Confirmed" value={`${confirmed} of ${defs.length}`} sub="canvases locked in" />
        <Tile icon="Gauge" tint="bg-(color:--step-1)" label="Overall progress" value={`${overall}%`} sub="of every required answer" />
        <Tile
          icon="SearchX"
          tint="bg-(color:--step-5)"
          label="Evidence Gaps"
          value={gaps.length}
          sub={gaps.length ? `${gapsG} guesses, ${gaps.length - gapsG} estimates` : 'nothing tagged E or G yet'}
        />
        <Tile icon="MessageSquareWarning" tint="bg-(color:--step-4)" label="Open objections" value={openRows.length} sub="in your Buy-in Ledger" />
      </div>

      {ARCS.map((arc) => (
        <section key={arc.name} className="space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h2 className={cn('font-heading text-lg font-semibold', arc.ink)}>{arc.name}</h2>
            <p className="text-sm text-muted-foreground">{arc.blurb}</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {list
              .filter((x) => x.def.no >= arc.from && x.def.no <= arc.to)
              .map((x) => (
                <CanvasCard key={x.def.no} def={x.def} state={x.st} />
              ))}
          </div>
        </section>
      ))}

      <div className="grid gap-5 lg:grid-cols-2">
        <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold">
              <ApperIcon name="SearchX" size={18} className="text-highlight" />
              Evidence Gaps so far
            </h2>
            <span className="text-xs text-muted-foreground">guesses first</span>
          </div>
          {gaps.length === 0 ? (
            <p className="text-sm text-muted-foreground">Numbers you tag E (estimated) or G (guess) collect here, so you know what to prove before you pitch.</p>
          ) : (
            <ul className="divide-y divide-border">
              {gaps.slice(0, 6).map((g, i) => (
                <li key={`${g.item}-${i}`} className="flex items-start gap-2.5 py-2">
                  <TagChip tag={g.tag} className="mt-0.5" />
                  <div className="min-w-0 text-sm">
                    <p className="leading-snug">{g.item}</p>
                    <p className="text-xs text-muted-foreground">{g.where}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {gaps.length > 6 && <p className="text-xs text-muted-foreground">and {gaps.length - 6} more in your pack.</p>}
        </section>

        <section className="space-y-3 rounded-2xl border border-border bg-card p-5 shadow-xs">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 font-heading text-base font-semibold">
              <ApperIcon name="MessageSquareWarning" size={18} className="text-primary" />
              Raise these first
            </h2>
            <span className="text-xs text-muted-foreground">top three open objections</span>
          </div>
          {agg.ledgerTop.length === 0 ? (
            <p className="text-sm text-muted-foreground">Objections from your sceptic rounds collect here. The three that matter most go to your Executive One-Pager.</p>
          ) : (
            <ul className="divide-y divide-border">
              {agg.ledgerTop.map((r, i) => (
                <li key={`${r.where}-${i}`} className="space-y-0.5 py-2 text-sm">
                  <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{r.where}</span>
                    <span>{r.sceptic}</span>
                    {r.star && <span className="rounded bg-highlight px-1.5 py-0.5 font-semibold text-highlight-foreground">Starred</span>}
                  </p>
                  <p className="leading-snug">{r.objection}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {touched[0] && stamp(touched[0].def.no) && (
        <p className="text-center text-xs text-muted-foreground">Last change: {formatRelativeTime(stamp(touched[0].def.no))}. Everything you type is saved.</p>
      )}
    </div>
  );
}
